import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Star, Quote, PenLine } from "lucide-react";
import { SectionHead } from "@/components/site/SectionHead";
import { ReviewForm } from "@/components/sections/ReviewForm";
import { BUSINESS, REVIEWS } from "@/content/site";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : null;

const useReviews = () => {
  const [list, setList] = useState(REVIEWS);
  useEffect(() => {
    if (!API) return undefined;
    let alive = true;
    axios.get(`${API}/reviews`).then((res) => {
      const real = Array.isArray(res.data) ? res.data : [];
      if (alive && real.length) setList(real.slice(0, 6));
    }).catch(() => {});
    return () => { alive = false; };
  }, []);
  return list;
};

export const Testimonials = () => {
  const reviews = useReviews();
  const [open, setOpen] = useState(false);
  return (
  <section data-testid="testimonials-section" className="relative bg-[#08090a] py-24 sm:py-32">
    <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
      <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
        <SectionHead index="09" eyebrow="Reviews" lines={["Quality you", "can trust"]} />
        <div className="flex flex-col gap-3 sm:flex-row">
          <button type="button" onClick={() => setOpen(true)} data-testid="leave-review-btn" className="btn btn-primary group">
            <PenLine className="relative z-10 h-4 w-4" /><span className="relative z-10">Leave a Review</span>
          </button>
          {BUSINESS.reviewsUrl && (
            <a href={BUSINESS.reviewsUrl} target="_blank" rel="noopener noreferrer" data-testid="reviews-cta" className="btn btn-ghost">See Our Reviews</a>
          )}
        </div>
      </div>
      <div className="mt-16 grid gap-4 md:grid-cols-3">
        {reviews.map((r, i) => (
          <motion.figure key={r.id || r.name} data-testid={`testimonial-card-${i + 1}`}
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.9, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="card-lux relative flex flex-col rounded-2xl p-8">
            {r.sample && <span className="absolute right-6 top-6 rounded-full border border-amber-400/40 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-amber-400" data-testid={`testimonial-sample-badge-${i + 1}`}>Sample review</span>}
            <Quote className="h-7 w-7 text-amber-400/60" />
            <div className="mt-6 flex gap-1" aria-label={`${r.rating ?? 5} out of 5 stars`}>
              {Array.from({ length: 5 }).map((_, k) => <Star key={k} className={`h-4 w-4 ${k < (r.rating ?? 5) ? "fill-amber-400 text-amber-400" : "text-zinc-600"}`} />)}
            </div>
            <blockquote className="mt-5 text-base leading-relaxed text-zinc-200">“{r.text}”</blockquote>
            <figcaption className="mt-auto pt-8">
              <div className="border-t border-white/8 pt-6">
                <p className="font-semibold text-white">{r.name}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-400">{r.service}{r.city ? ` · ${r.city}, TX` : ""}</p>
              </div>
            </figcaption>
          </motion.figure>
        ))}
      </div>
    </div>
    <ReviewForm open={open} onOpenChange={setOpen} />
  </section>
  );
};
